import React, { useState } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { McqInteraction } from '../../types/page';
import { isMcqInteraction } from '../../types/page';
import { ConfettiBurst } from '../components/ConfettiBurst';
import { Button } from '../../../../components/ui/Button';

interface McqImageV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

function getAssetUrl(
  assets: Array<{ id?: string; asset_id?: string; url?: string }> | undefined,
  assetId: string
): string | null {
  const a = assets?.find((x) => x.id === assetId || x.asset_id === assetId);
  return a?.url ?? null;
}

export const McqImageV1: React.FC<McqImageV1Props> = ({ page, onComplete }) => {
  const interactions = page.page_json_v1?.interactions ?? [];
  const assets = page.page_json_v1?.assets ?? [];
  const mcq = interactions.find(isMcqInteraction) as McqInteraction | undefined;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completed, setCompleted] = useState(false);

  if (!mcq) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No MCQ image interaction found.
      </div>
    );
  }

  const options = mcq.options ?? [];
  const correctId = mcq.answer_option_id;
  const isCorrect = selectedId === correctId;

  const handleSubmit = () => {
    if (selectedId === null) return;
    setSubmitted(true);
    if (selectedId === correctId) {
      setShowConfetti(true);
      setCompleted(true);
      onComplete?.();
    }
  };

  const getOptionStyle = (optId: string) => {
    if (!submitted) {
      return selectedId === optId
        ? 'border-primary-500 ring-2 ring-primary-300'
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300';
    }
    if (optId === correctId) return 'border-green-500 ring-2 ring-green-300';
    if (optId === selectedId && optId !== correctId) return 'border-red-500 ring-2 ring-red-300';
    return 'border-gray-200 dark:border-gray-700 opacity-60';
  };

  return (
    <div className="space-y-6 p-6 overflow-auto">
      <ConfettiBurst trigger={showConfetti} duration={1500} />
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || 'Select the correct image'}
      </h1>
      <p className="text-gray-700 dark:text-gray-300">{mcq.prompt}</p>
      <div className="grid grid-cols-2 gap-4">
        {options.map((opt) => {
          const imgUrl = opt.image_asset_id ? getAssetUrl(assets, opt.image_asset_id) : null;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => !submitted && setSelectedId(opt.id)}
              disabled={submitted}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${getOptionStyle(opt.id)} ${
                submitted ? 'cursor-default' : 'cursor-pointer'
              }`}
            >
              <div className="w-24 h-24 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                {imgUrl ? (
                  <img src={imgUrl} alt={opt.text ?? opt.id} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-semibold text-gray-500">{opt.text ?? opt.id}</span>
                )}
              </div>
              {opt.text && (
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{opt.text}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={selectedId === null || submitted}
        >
          Submit
        </Button>
        {submitted && !isCorrect && (
          <span className="text-red-600 dark:text-red-400 font-medium">Wrong. Try again.</span>
        )}
        {submitted && isCorrect && (
          <span className="text-green-600 dark:text-green-400 font-medium">Correct!</span>
        )}
      </div>
    </div>
  );
};
